<template>
  <div class="reports-invoices-page">
    <ReportsPageShell
      :title="currentHeaderTitle"
      :subtitle="currentHeaderSubtitle"
      :actions="headerActions"
      @action="onHeaderAction"
    >
      <template #income-tabs>
        <ReportsPrimaryTabs
          :tabs="reportTabs"
          :active-tab="activeReportTab"
          @select="handleReportTabSelect"
        />
      </template>

      <template #metrics>
        <ReportsKpiSection
          :cards="kpiCards"
          :desktop-columns="4"
          :tablet-columns="2"
        />
      </template>

      <template #toolbar>
        <ReportsFilterToolbar
          report-key="invoices"
          :report-tab="activeReportTab"
          :selectors-fetcher="fetchInvoicesSelectors"
          @filters-change="handleToolbarFiltersChange"
          @apply="handleApplyFilters"
        />
      </template>

      <template #chart>
        <ReportsChartSection
          :mode="chartMode"
          :points="chartPoints"
          :allow-visibility-toggle="true"
          visibility-storage-key="reports.invoices.chart.hidden"
          @update:mode="chartMode = $event"
        />
      </template>

      <template #table>
        <ReportsTable
          :table="tableData"
          sticky-header
          :allow-totals-detail="true"
          :sort-direction="activeSortDirection"
          @period-cell-click="handlePeriodCellClick"
        />
      </template>
    </ReportsPageShell>

    <ReportsDetailModal
      :show="detailModal.show"
      :loading="detailModal.loading"
      :title="detailModal.title"
      :context-line="detailModal.contextLine"
      :subtitle="detailModal.subtitle"
      :columns="detailModal.columns"
      :rows="detailModal.rows"
      :grand-total="detailModal.grandTotal"
      @close="closeDetailModal"
    />
  </div>
</template>

<script>
import ReportsPageShell from '@/components/reports/ReportsPageShell.vue';
import ReportsPrimaryTabs from '@/components/reports/ReportsPrimaryTabs.vue';
import ReportsKpiSection from '@/components/reports/ReportsKpiSection.vue';
import ReportsFilterToolbar from '@/components/reports/ReportsFilterToolbar.vue';
import ReportsChartSection from '@/components/reports/ReportsChartSection.vue';
import ReportsTable from '@/components/reports/ReportsTable.vue';
import ReportsDetailModal from '@/components/reports/ReportsDetailModal.vue';
import reportsInvoicesService from '@/services/reportsInvoices';
import { extractPayload } from '@/utils/dataNormalization';
import { mapReportsChartPoints } from '@/composables/reports/general/useReportsPresentation';
import { buildFileTimestampSegment, downloadBlobFile } from '@/utils/fileDownload';
import {
  REPORTS_SALES_TEXT,
  requireReportsSalesTextValue,
  normalizeReportsMetricCards,
  normalizeReportsTablePayload,
  buildReportsSalesHeaderActions,
} from '@/composables/reports/sales/useReportsSalesPresentation';
import {
  REPORTS_SORT_DIRECTION_NONE,
  hasReportsToolbarNonSortChanges,
  normalizeReportsToolbarFiltersPayload,
  resolveReportsToolbarSortChange,
} from '@/composables/reports/general/toolbar/useReportsFilterToolbarHelpers';

const INVOICES_REPORT_CODE = 'invoices_issued';
const INVOICES_EXPORTABLE_CODES = [INVOICES_REPORT_CODE];
const INVOICES_HEADER_TITLE = 'Reportes de facturación';

const INVOICES_FALLBACK_TAB = {
  key: INVOICES_REPORT_CODE,
  label: 'Facturas realizadas',
  description: 'Seguimiento de facturas emitidas en el periodo.',
  headerTitle: INVOICES_HEADER_TITLE,
  isDefault: true,
};

export default {
  name: 'ReportsInvoicesPage',
  components: {
    ReportsPageShell,
    ReportsPrimaryTabs,
    ReportsKpiSection,
    ReportsFilterToolbar,
    ReportsChartSection,
    ReportsTable,
    ReportsDetailModal,
  },
  data() {
    return {
      chartMode: 'bar',
      activeReportTab: INVOICES_REPORT_CODE,
      reportTabs: [INVOICES_FALLBACK_TAB],
      headerActions: buildReportsSalesHeaderActions(),
      lastAppliedFilters: null,
      rawMetricsCards: [],
      chartPointsData: [],
      tableData: {
        headers: [],
        rows: [],
        period_totals: [],
        grand_total: 0,
      },
      detailModal: {
        show: false,
        loading: false,
        title: '',
        contextLine: '',
        subtitle: '',
        columns: [],
        rows: [],
        grandTotal: 0,
      },
      activeSortDirection: REPORTS_SORT_DIRECTION_NONE,
    };
  },
  async mounted() {
    await this.loadReportTabs();
    await this.refreshReportData();
  },
  computed: {
    currentTabConfig() {
      const match = this.reportTabs.find((tab) => tab.key === this.activeReportTab);
      return match || this.reportTabs[0] || INVOICES_FALLBACK_TAB;
    },
    currentHeaderTitle() {
      return this.currentTabConfig?.headerTitle || INVOICES_HEADER_TITLE;
    },
    currentHeaderSubtitle() {
      const tabLabel = String(this.currentTabConfig?.label || '').trim();
      return tabLabel !== '' ? `Reporte de ${tabLabel}` : 'Reporte de facturación';
    },
    kpiCards() {
      return normalizeReportsMetricCards(this.rawMetricsCards);
    },
    chartPoints() {
      return this.chartPointsData;
    },
  },
  methods: {
    resolveInvoicesReportCode(value) {
      return String(value || '').trim() === INVOICES_REPORT_CODE
        ? INVOICES_REPORT_CODE
        : INVOICES_REPORT_CODE;
    },
    mapBackendTabs(rawTabs = []) {
      const source = Array.isArray(rawTabs) ? rawTabs : [];
      const mapped = source
        .map((tab) => {
          const name = String(tab?.name || '').trim();
          if (name === '') {
            return null;
          }

          return {
            key: this.resolveInvoicesReportCode(tab?.code),
            label: name,
            description: String(tab?.description || '').trim(),
            headerTitle: INVOICES_HEADER_TITLE,
            isDefault: tab?.is_default === true || Number(tab?.is_default) === 1,
          };
        })
        .filter(Boolean);

      return mapped.length > 0 ? mapped : [INVOICES_FALLBACK_TAB];
    },
    async loadReportTabs() {
      try {
        const response = await reportsInvoicesService.selectors(
          { report: this.resolveInvoicesReportCode(this.activeReportTab) },
          { skipLoading: true }
        );
        const payload = extractPayload(response);
        const allTabs = this.mapBackendTabs(payload?.reports || payload?.tabs);

        this.reportTabs = allTabs;

        const defaultTab = allTabs.find((tab) => tab?.isDefault);
        if (defaultTab) {
          this.activeReportTab = this.resolveInvoicesReportCode(defaultTab.key);
          return;
        }

        const isActiveTabValid = allTabs.some((tab) => tab.key === this.activeReportTab);
        if (!isActiveTabValid) {
          this.activeReportTab = String(allTabs[0]?.key || INVOICES_REPORT_CODE);
        }
      } catch (error) {
        this.reportTabs = [INVOICES_FALLBACK_TAB];
        this.activeReportTab = INVOICES_REPORT_CODE;
      }
    },
    buildRequestParams() {
      const filters = this.lastAppliedFilters && typeof this.lastAppliedFilters === 'object'
        ? { ...this.lastAppliedFilters }
        : {};
      delete filters.sort_direction;

      return {
        ...filters,
        report: this.resolveInvoicesReportCode(this.activeReportTab),
      };
    },
    handleToolbarFiltersChange(payload, meta = {}) {
      const { sortDirection, nextFilters } = resolveReportsToolbarSortChange({
        payload,
        meta,
        currentFilters: this.lastAppliedFilters,
      });

      this.activeSortDirection = sortDirection;
      this.lastAppliedFilters = nextFilters;
    },
    async fetchInvoicesSelectors(params, config) {
      const requestedReport = this.resolveInvoicesReportCode(
        params?.report_tab || params?.report || this.activeReportTab
      );

      return reportsInvoicesService.selectors(
        {
          ...params,
          report: requestedReport,
        },
        config
      );
    },
    async fetchReportData({ skipLoading = false } = {}) {
      const requestConfig = skipLoading ? { skipLoading: true } : {};
      const response = await reportsInvoicesService.table(this.buildRequestParams(), requestConfig);
      const payload = extractPayload(response);

      this.rawMetricsCards = Array.isArray(payload?.cards) ? payload.cards : [];
      this.tableData = normalizeReportsTablePayload(payload?.table || {});
      this.chartPointsData = mapReportsChartPoints(payload?.chart || {});
    },
    async refreshReportData({ skipLoading = false } = {}) {
      try {
        await this.fetchReportData({ skipLoading });
      } catch (error) {
        this.rawMetricsCards = [];
        this.tableData = normalizeReportsTablePayload({});
        this.chartPointsData = [];
      }
    },
    async handleReportTabSelect(tabKey) {
      const nextTab = this.resolveInvoicesReportCode(tabKey);
      if (nextTab === '' || nextTab === this.activeReportTab) {
        return;
      }

      this.closeDetailModal();
      this.activeReportTab = nextTab;
      await this.refreshReportData();
    },
    buildExportFileName(extension) {
      const normalizedExtension = String(extension || '').trim().toLowerCase();
      return `reporte_facturacion_${buildFileTimestampSegment()}.${normalizedExtension}`;
    },
    async onHeaderAction(action) {
      const actionKey = String(action?.key || '').trim().toLowerCase();
      if (!['excel', 'pdf'].includes(actionKey)) {
        return;
      }

      const reportCode = this.resolveInvoicesReportCode(this.activeReportTab);
      if (!INVOICES_EXPORTABLE_CODES.includes(reportCode)) {
        this.$toast?.info?.(
          requireReportsSalesTextValue(
            REPORTS_SALES_TEXT.page.toasts.exportUnavailable,
            'reports.sales.page.toasts.exportUnavailable'
          )
        );
        return;
      }

      try {
        const params = this.buildRequestParams();
        const response = actionKey === 'excel'
          ? await reportsInvoicesService.excel(params)
          : await reportsInvoicesService.pdf(params);
        const extension = actionKey === 'excel' ? 'xlsx' : 'pdf';
        const mimeType = actionKey === 'excel'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf';

        downloadBlobFile(response, this.buildExportFileName(extension), mimeType);

        this.$toast?.success(
          actionKey === 'excel'
            ? requireReportsSalesTextValue(REPORTS_SALES_TEXT.page.toasts.excelSuccess, 'reports.sales.page.toasts.excelSuccess')
            : requireReportsSalesTextValue(REPORTS_SALES_TEXT.page.toasts.pdfSuccess, 'reports.sales.page.toasts.pdfSuccess')
        );
      } catch (error) {
        this.$toast?.error(
          error?.response?.data?.message
            || error?.message
            || (actionKey === 'excel'
              ? requireReportsSalesTextValue(REPORTS_SALES_TEXT.page.toasts.excelError, 'reports.sales.page.toasts.excelError')
              : requireReportsSalesTextValue(REPORTS_SALES_TEXT.page.toasts.pdfError, 'reports.sales.page.toasts.pdfError'))
        );
      }
    },
    async handleApplyFilters(payload) {
      this.closeDetailModal();

      const nextPayload = normalizeReportsToolbarFiltersPayload(payload);
      const shouldReloadData = hasReportsToolbarNonSortChanges(nextPayload, this.lastAppliedFilters);

      this.lastAppliedFilters = nextPayload;
      this.activeSortDirection = nextPayload.sort_direction;

      if (shouldReloadData) {
        await this.refreshReportData({ skipLoading: false });
      }
    },
    closeDetailModal() {
      this.detailModal = {
        ...this.detailModal,
        show: false,
      };
    },
    buildDetailRequestParams(payload = {}) {
      const requestParams = {
        ...this.buildRequestParams(),
      };

      if (Array.isArray(payload.element_ids) && payload.element_ids.length > 0) {
        requestParams.element_ids = payload.element_ids;
      }

      const detailSource = String(payload.detail_source || '').trim().toLowerCase();
      if (detailSource !== '') {
        requestParams.detail_source = detailSource;
      }

      const detailMetric = String(payload.detail_metric || '').trim().toLowerCase();
      if (detailMetric !== '') {
        requestParams.detail_metric = detailMetric;
      }

      return requestParams;
    },
    buildDetailContextLine(payload = {}) {
      const primaryPivotName = String(payload?.primary_pivot_name || '').trim();
      const secondaryPivotName = String(payload?.secondary_pivot_name || '').trim();
      const columnLabel = String(payload?.column_label || payload?.metric_label || '').trim();

      return [primaryPivotName, secondaryPivotName, columnLabel]
        .filter((value) => value !== '')
        .join(' - ');
    },
    async handlePeriodCellClick(payload) {
      if (!Array.isArray(payload?.element_ids) || payload.element_ids.length === 0) {
        return;
      }

      const metricLabel = String(payload?.metric_label || '').trim();
      const reportLabel = this.currentTabConfig?.label
        || requireReportsSalesTextValue(REPORTS_SALES_TEXT.detail.defaultReportLabel, 'reports.sales.detail.defaultReportLabel');
      const detailTitlePrefix = requireReportsSalesTextValue(REPORTS_SALES_TEXT.detail.titlePrefix, 'reports.sales.detail.titlePrefix');
      const detailTitle = metricLabel !== ''
        ? `${detailTitlePrefix} ${metricLabel}`
        : `${detailTitlePrefix} ${reportLabel}`;
      const detailContextLine = this.buildDetailContextLine(payload);

      this.detailModal = {
        ...this.detailModal,
        show: true,
        loading: true,
        title: detailTitle,
        contextLine: detailContextLine,
        subtitle: requireReportsSalesTextValue(REPORTS_SALES_TEXT.detail.loadingSubtitle, 'reports.sales.detail.loadingSubtitle'),
        columns: [],
        rows: [],
        grandTotal: 0,
      };

      try {
        const response = await reportsInvoicesService.detail(
          this.buildDetailRequestParams(payload),
          { skipLoading: true }
        );
        const data = extractPayload(response);
        const detail = data?.detail || {};
        const columns = Array.isArray(detail?.columns) ? detail.columns : [];
        const rows = Array.isArray(detail?.rows) ? detail.rows : [];
        const count = Number(detail?.count || rows.length || 0);

        this.detailModal = {
          ...this.detailModal,
          loading: false,
          title: detailTitle,
          contextLine: detailContextLine,
          subtitle: `${count} ${count === 1
            ? requireReportsSalesTextValue(REPORTS_SALES_TEXT.detail.recordLabelSingular, 'reports.sales.detail.recordLabelSingular')
            : requireReportsSalesTextValue(REPORTS_SALES_TEXT.detail.recordLabelPlural, 'reports.sales.detail.recordLabelPlural')}`,
          columns,
          rows,
          grandTotal: Number(detail?.grand_total ?? 0),
        };
      } catch (error) {
        this.detailModal = {
          ...this.detailModal,
          loading: false,
          contextLine: detailContextLine,
          subtitle: requireReportsSalesTextValue(REPORTS_SALES_TEXT.detail.loadErrorSubtitle, 'reports.sales.detail.loadErrorSubtitle'),
          columns: [],
          rows: [],
          grandTotal: 0,
        };
      }
    },
  },
};
</script>
